import { AbhaHeroModalStep, AbhaJourneyNodeId } from '@components/NewProduct/AbhaHeroWidget/enums'
import { DEFAULT_ABHA_CARD_PREVIEW } from '@components/NewProduct/constants'
import {
  ABHA_MASKED_NUMBER_DISPLAY,
  formatAbhaNumberForDisplay,
  sanitizeAadhaar,
  type AbhaCardPreviewData,
} from '@helpers/abhaWidgetHelper'

export type AbhaTransactionType = 'PROPOSAL' | 'POLICY' | 'CENTRAL_USER'

export type AbhaEntryParams = Record<string, string | undefined>

export type AbhaUserName = {
  first_name?: string
  middle_name?: string
  last_name?: string
}

export type AbhaMemberListDetail = {
  id?: string
  selected?: boolean
  consent?: boolean
  aadhaar?: string
  phone?: string
  phone_linked?: boolean
  txn_id?: string
  relationship?: string
  masked_phone_number?: string
  otp?: string
  abha_id?: string
  abha_address?: string
  dob?: string
  gender?: string
  profile_photo?: string
  name?: AbhaUserName | string
  account_index?: number | string
  search_txn_id?: string
  token?: string
  status?: string
  abha_card?: string | Record<string, unknown>
  abha_card_png?: string
  abha_qr_code?: string
  abha_user_details?: {
    name?: AbhaUserName
    dob?: string
    gender?: string
    profile_photo?: string
    ABHANumber?: string
    preferredAbhaAddress?: string
    [key: string]: unknown
  }
  error?: Record<string, unknown>
  [key: string]: unknown
}

export type AbhaSessionState = {
  transaction_id: string
  token: string
  redirect_url: string
  client_id: string
  abha_member_list_details: AbhaMemberListDetail[]
  current_node_id: string
  next_node_id: string
  error: Record<string, unknown>
  abha_id?: string
  abha_address?: string
  abha_address_suggestions?: string[]
  abha_card?: Record<string, unknown>
  abha_card_png?: string
  abha_user_details?: {
    name?: AbhaUserName
    dob?: string
    gender?: string
    profile_photo?: string
    [key: string]: unknown
  }
  dob?: string
  gender?: string
  profile_photo?: string
  masked_phone_number?: string
}

export type AbhaJourneyPayload = {
  proposal_id?: string
  policy_id?: string
  abha_transaction_type: AbhaTransactionType
  utm_source?: string
  invitation_id?: string
  abha_member_details: {
    abha_phone: string
    abha_aadhar?: string
    relationship?: string
  }
  client_redirect_url?: string
  is_new_flow: true
  journey_type?: 'abha_mobile_fetch' | 'abha_multiple_self'
}

export type AbhaJourneyResponse = {
  redirect_url?: string
  message?: string
}

export type AbhaNextStepResponse = {
  status?: number
  message?: string
  abha_member_list_details?: AbhaMemberListDetail[]
  current_node_id?: string
  next_node_id?: string
  error?: Record<string, unknown>
  abha_id?: string
  abha_address?: string
  abha_address_suggestions?: string[]
  abha_card?: Record<string, unknown>
  abha_card_png?: string
  abha_user_details?: AbhaSessionState['abha_user_details']
  dob?: string
  gender?: string
  profile_photo?: string
  masked_phone_number?: string
}

export type ExistingAbhaSearchResult = {
  session: AbhaSessionState
  abhaMemberListDetails: AbhaMemberListDetail[]
}

export class AbhaFlowError extends Error {
  status?: number

  code?: 'OTP_INVALID' | 'GENERIC'

  constructor(message: string, options?: { status?: number; code?: 'OTP_INVALID' | 'GENERIC' }) {
    super(message)
    this.name = 'AbhaFlowError'
    this.status = options?.status
    this.code = options?.code ?? 'GENERIC'
  }
}

export const isAbhaOtpInvalidError = (error: unknown): error is AbhaFlowError =>
  error instanceof AbhaFlowError && error.code === 'OTP_INVALID'

export const firstValue = (value: string | undefined) => value ?? undefined

export const createDefaultAbhaSessionState = (): AbhaSessionState => ({
  transaction_id: '',
  token: '',
  redirect_url: '',
  client_id: '',
  abha_member_list_details: [],
  current_node_id: '',
  next_node_id: '',
  error: {},
})

const hasNonEmptyRecord = (value?: Record<string, unknown>) => Boolean(value && Object.keys(value).length > 0)

export const hasAbhaStepError = (response: AbhaNextStepResponse, session?: AbhaSessionState): boolean => {
  if (hasNonEmptyRecord(response.error)) {
    return true
  }

  const member = response.abha_member_list_details?.[0] ?? session?.abha_member_list_details?.[0]

  return hasNonEmptyRecord(member?.error)
}

const formatAbhaUserName = (name?: AbhaUserName | string) => {
  if (!name) return ''

  if (typeof name === 'string') {
    return name
  }

  return [name.first_name, name.middle_name, name.last_name].filter(Boolean).join(' ').trim()
}

const resolveProfileImageUrl = (profilePhoto?: string) => {
  if (!profilePhoto) return undefined

  if (profilePhoto.startsWith('data:') || profilePhoto.startsWith('http')) {
    return profilePhoto
  }

  return `data:image/jpeg;base64,${profilePhoto}`
}

export const buildCardPreviewFromSession = (session: AbhaSessionState): AbhaCardPreviewData => {
  const member = session.abha_member_list_details[0]
  const userDetails = member.abha_user_details
  const cardPayload = session.abha_card && typeof session.abha_card === 'object' ? session.abha_card : undefined

  const name =
    formatAbhaUserName(userDetails?.name) ||
    (typeof member?.name === 'string' ? member.name : formatAbhaUserName(member?.name)) ||
    (typeof cardPayload?.name === 'string' ? cardPayload.name : DEFAULT_ABHA_CARD_PREVIEW.name)

  const dateOfBirth =
    session.dob ||
    userDetails?.dob ||
    (typeof member?.dob === 'string' ? member.dob : '') ||
    (typeof cardPayload?.dob === 'string' ? cardPayload.dob : DEFAULT_ABHA_CARD_PREVIEW.dateOfBirth)

  const rawAbhaId =
    session.abha_id ||
    (typeof member?.abha_id === 'string' ? member.abha_id : '') ||
    (typeof cardPayload?.abha_id === 'string' ? cardPayload.abha_id : '')

  const abhaNumber = formatAbhaNumberForDisplay(rawAbhaId) || undefined

  const abhaAddress =
    session.abha_address ||
    (typeof member?.abha_address === 'string' ? member.abha_address : '') ||
    (typeof cardPayload?.abha_address === 'string' ? cardPayload.abha_address : DEFAULT_ABHA_CARD_PREVIEW.abhaAddress)

  const profileImageUrl =
    resolveProfileImageUrl(session.profile_photo) ||
    resolveProfileImageUrl(userDetails?.profile_photo) ||
    resolveProfileImageUrl(typeof member?.profile_photo === 'string' ? member.profile_photo : undefined) ||
    resolveProfileImageUrl(session.abha_card_png) ||
    DEFAULT_ABHA_CARD_PREVIEW.profileImageUrl

  return {
    name,
    dateOfBirth,
    maskedAbhaNumber: ABHA_MASKED_NUMBER_DISPLAY,
    abhaNumber,
    abhaAddress,
    profileImageUrl,
    relationshipLabel: 'You',
  }
}

export const buildCardPreviewFromMember = (member: AbhaMemberListDetail): AbhaCardPreviewData => {
  const userDetails = member?.abha_user_details
  const rawAbhaId = member?.abha_id ?? userDetails?.ABHANumber ?? ''
  const isVerified = Boolean(member?.token)

  return {
    name:
      formatAbhaUserName(member?.name) ||
      formatAbhaUserName(userDetails?.name) ||
      `ABHA card ${member?.account_index ?? ''}`.trim(),
    dateOfBirth: isVerified ? member?.dob ?? userDetails?.dob ?? '' : '',
    maskedAbhaNumber: formatAbhaNumberForDisplay(rawAbhaId) || ABHA_MASKED_NUMBER_DISPLAY,
    abhaNumber: isVerified ? formatAbhaNumberForDisplay(rawAbhaId) || undefined : undefined,
    abhaAddress: isVerified ? member?.abha_address ?? userDetails?.preferredAbhaAddress ?? '' : '',
    profileImageUrl: resolveProfileImageUrl(member?.profile_photo),
    relationshipLabel: undefined,
  }
}

export const getTransactionType = (params: AbhaEntryParams): AbhaTransactionType => {
  if (firstValue(params.proposal_id)) {
    return 'PROPOSAL'
  }

  if (firstValue(params.policy_id)) {
    return 'POLICY'
  }

  return 'CENTRAL_USER'
}

export const getPrefillAadhaarFromSearchParams = (searchParams: Record<string, string>) => {
  const rawAadhaar = searchParams.aadhaar || searchParams.aadhar || ''

  return rawAadhaar ? sanitizeAadhaar(rawAadhaar) : ''
}

export const parseAbhaEnrolRedirectUrl = (enrolRedirectUrl: string): AbhaSessionState => {
  try {
    const url = new URL(enrolRedirectUrl)

    return {
      transaction_id: url.searchParams.get('transaction_id') ?? '',
      token: url.searchParams.get('token') ?? '',
      redirect_url: url.searchParams.get('redirect_url') ?? '',
      client_id: url.searchParams.get('client_id') ?? '',
      abha_member_list_details: [],
      current_node_id: '',
      next_node_id: '',
      error: {},
    }
  } catch {
    return createDefaultAbhaSessionState()
  }
}

export const mergeAbhaSessionState = (session: AbhaSessionState, response: AbhaNextStepResponse): AbhaSessionState => ({
  ...session,
  abha_id: response.abha_id ?? session.abha_id,
  abha_address: response.abha_address ?? session.abha_address,
  abha_address_suggestions: response.abha_address_suggestions ?? session.abha_address_suggestions,
  abha_card: response.abha_card ?? session.abha_card,
  abha_card_png: response.abha_card_png ?? session.abha_card_png,
  abha_user_details: response.abha_user_details ?? session.abha_user_details,
  dob: response.dob ?? session.dob,
  gender: response.gender ?? session.gender,
  profile_photo: response.profile_photo ?? session.profile_photo,
  masked_phone_number: response.masked_phone_number ?? session.masked_phone_number,
  abha_member_list_details: response.abha_member_list_details ?? session.abha_member_list_details,
  current_node_id: response.current_node_id ?? session.current_node_id,
  next_node_id: response.next_node_id ?? session.next_node_id,
  error: response.error ?? session.error,
})

export const buildTransactionPayload = ({
  phone,
  aadhaar,
  searchParams,
  relationship,
}: {
  phone: string
  aadhaar?: string
  searchParams: AbhaEntryParams
  relationship?: string
}): AbhaJourneyPayload => {
  const transactionType = getTransactionType(searchParams)
  const proposalId = firstValue(searchParams.proposal_id)
  const policyId = firstValue(searchParams.policy_id)
  const utmSource = firstValue(searchParams.utm_source)
  const invitationId = firstValue(searchParams.invitation_id)
  const clientRedirectUrl = firstValue(searchParams.redirect_url)
  const sanitizedAadhaar = aadhaar ? sanitizeAadhaar(aadhaar) : ''

  return {
    ...(transactionType === 'PROPOSAL' && proposalId ? { proposal_id: proposalId } : {}),
    ...(transactionType === 'POLICY' && policyId ? { policy_id: policyId } : {}),
    abha_transaction_type: transactionType,
    ...(utmSource ? { utm_source: utmSource } : {}),
    ...(invitationId ? { invitation_id: invitationId } : {}),
    abha_member_details: {
      abha_phone: phone,
      ...(sanitizedAadhaar ? { abha_aadhar: sanitizedAadhaar } : {}),
      ...(relationship ? { relationship } : {}),
    },
    ...(clientRedirectUrl ? { client_redirect_url: clientRedirectUrl } : {}),
    is_new_flow: true,
  }
}

export const resolveMaskedPhoneNumberFromSession = (
  session: AbhaSessionState | null | undefined
): string | undefined => {
  if (!session) return undefined

  if (session.masked_phone_number) {
    return session.masked_phone_number
  }

  const memberWithMaskedPhone = session.abha_member_list_details?.find(member => member.masked_phone_number)

  return memberWithMaskedPhone?.masked_phone_number
}

export const createAbhaJourney = async (payload: AbhaJourneyPayload): Promise<AbhaJourneyResponse> => {
  const response = await fetch('/api/abha/journey/transactions/', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    redirect: 'follow',
    body: JSON.stringify(payload),
  })

  const data = (await response.json().catch(() => ({}))) as AbhaJourneyResponse

  if (response.status !== 200) {
    throw new AbhaFlowError(data?.message || response.statusText || 'Unable to start ABHA journey')
  }

  return data
}

const serializeMemberListDetails = (members: AbhaMemberListDetail[]) =>
  members.map(item => ({
    ...item,
    consent: item.consent ?? item.selected ?? false,
  }))

export const getAbhaNextStep = async (
  session: Pick<AbhaSessionState, 'transaction_id' | 'token'>,
  body: {
    current_node_id: string
    abha_member_list_details: AbhaMemberListDetail[]
  },
  headers: Record<string, string> = { journey: 'abha_creation' }
): Promise<AbhaNextStepResponse> => {
  const response = await fetch('/api/abha/next-step/', {
    headers: {
      'Content-Type': 'application/json',
      Authorization: session.token,
      'transaction-id': session.transaction_id,
      ...headers,
    },
    redirect: 'follow',
    method: 'POST',
    body: JSON.stringify({
      current_node_id: body.current_node_id,
      abha_member_list_details: serializeMemberListDetails(body.abha_member_list_details),
    }),
  })

  if (response.status === 200) {
    return (await response.json()) as AbhaNextStepResponse
  }

  if (response.status === 422 || response.status === 429) {
    const errorBody = (await response.json().catch(() => ({}))) as AbhaNextStepResponse

    if (hasAbhaStepError(errorBody)) {
      throw new AbhaFlowError(
        sanitizeAbhaErrorMessage(null, mergeAbhaSessionState(createDefaultAbhaSessionState(), errorBody)),
        {
          status: response.status,
        }
      )
    }

    return { ...errorBody, status: response.status }
  }

  const errorBody = (await response.json().catch(() => ({}))) as AbhaNextStepResponse

  throw new AbhaFlowError(
    JSON.stringify({
      status: response.status,
      message: response.statusText,
      data: errorBody,
    }),
    { status: response.status }
  )
}

export const advanceAbhaNextStep = async (
  session: AbhaSessionState,
  currentNodeId: string,
  memberListDetails?: AbhaMemberListDetail[]
): Promise<{ session: AbhaSessionState; response: AbhaNextStepResponse }> => {
  const details = memberListDetails ?? session.abha_member_list_details
  const response = await getAbhaNextStep(session, {
    current_node_id: currentNodeId,
    abha_member_list_details: details,
  })

  if (response?.status === 401) {
    throw new AbhaFlowError('Your session has expired. Please start again.', { status: 401 })
  }

  const mergedSession = mergeAbhaSessionState(session, response)

  if (
    response?.next_node_id === AbhaJourneyNodeId.OtpInvalid ||
    response?.current_node_id === AbhaJourneyNodeId.OtpInvalid
  ) {
    throw new AbhaFlowError('Invalid OTP. Please try again.', { code: 'OTP_INVALID' })
  }

  if (hasAbhaStepError(response, mergedSession)) {
    throw new AbhaFlowError(sanitizeAbhaErrorMessage(null, mergedSession))
  }

  return { session: mergedSession, response }
}

export const resolveModalStepFromNodeId = (nodeId: string): AbhaHeroModalStep => {
  switch (nodeId) {
    case AbhaJourneyNodeId.AadhaarInputV2:
      return AbhaHeroModalStep.Aadhaar
    case AbhaJourneyNodeId.OtpVerificationV2:
    case AbhaJourneyNodeId.MobileOtpVerificationV2:
      return AbhaHeroModalStep.Otp
    case AbhaJourneyNodeId.CreationV2:
      return AbhaHeroModalStep.Prepare
    case AbhaJourneyNodeId.AlreadyExists:
      return AbhaHeroModalStep.Success
    case AbhaJourneyNodeId.OtpInvalid:
      return AbhaHeroModalStep.Otp
    default:
      if (nodeId.includes('success') || nodeId.includes('complete')) {
        return AbhaHeroModalStep.Success
      }

      return AbhaHeroModalStep.Error
  }
}

export const buildMemberListWithAadhaar = (
  members: AbhaMemberListDetail[],
  aadhaar: string,
  consent: boolean,
  phone: string
): AbhaMemberListDetail[] => {
  const sanitizedAadhaar = sanitizeAadhaar(aadhaar)

  if (members.length > 0) {
    return members.map((member, index) =>
      index === 0
        ? {
            ...member,
            aadhaar: sanitizedAadhaar,
            phone,
            selected: true,
            consent,
          }
        : member
    )
  }

  return [
    {
      selected: true,
      aadhaar: sanitizedAadhaar,
      phone,
      phone_linked: false,
      consent,
      relationship: 'SELF',
    },
  ]
}

export const buildMemberListWithOtp = (members: AbhaMemberListDetail[], otp: string): AbhaMemberListDetail[] => {
  if (members.length > 0) {
    return members.map((member, index) => (index === 0 ? { ...member, otp } : member))
  }

  return [{ otp, selected: true, consent: true }]
}

export const buildMemberListWithPhone = (members: AbhaMemberListDetail[], phone: string): AbhaMemberListDetail[] => {
  if (members.length > 0) {
    return members.map((member, index) =>
      index === 0
        ? {
            ...member,
            phone,
            selected: true,
          }
        : member
    )
  }

  return [{ phone, selected: true, consent: true }]
}

export const sanitizeAbhaErrorMessage = (error: unknown, session?: AbhaSessionState): string => {
  if (isAbhaOtpInvalidError(error)) {
    return error.message
  }

  if (error instanceof AbhaFlowError) {
    try {
      const parsed = JSON.parse(error.message) as { data?: { message?: string }; message?: string }
      return parsed?.data?.message || parsed?.message || error.message
    } catch {
      return error.message
    }
  }

  const memberError = session?.abha_member_list_details?.[0]?.error as
    | { details?: Array<{ message?: string }>; message?: string }
    | undefined
  const detailMessage = memberError?.details?.[0]?.message || memberError?.message

  if (detailMessage) {
    return detailMessage
  }

  if (hasNonEmptyRecord(session?.error)) {
    const sessionMessage = session?.error?.message

    if (typeof sessionMessage === 'string' && sessionMessage) {
      return sessionMessage
    }
  }

  if (error instanceof Error && error.message) {
    try {
      const parsed = JSON.parse(error.message) as { data?: { message?: string }; message?: string }
      return parsed?.data?.message || parsed?.message || 'Something went wrong. Please try again.'
    } catch {
      return error.message
    }
  }

  return 'Something went wrong. Please try again.'
}

export const startAbhaResolveCreateJourney = async ({
  phone,
  relationship,
  searchParams = {},
}: {
  phone: string
  relationship: string
  searchParams?: AbhaEntryParams
}): Promise<{ session: AbhaSessionState; modalStep: AbhaHeroModalStep }> => {
  const journeyResponse = await createAbhaJourney(buildTransactionPayload({ phone, searchParams, relationship }))

  if (!journeyResponse.redirect_url) {
    throw new AbhaFlowError('Unable to start ABHA journey. Please try again.')
  }

  const initialSession = parseAbhaEnrolRedirectUrl(journeyResponse.redirect_url)
  const { session } = await advanceAbhaNextStep(initialSession, '')
  const modalStep = resolveModalStepFromNodeId(session.next_node_id)

  if (modalStep === AbhaHeroModalStep.Error) {
    throw new AbhaFlowError(sanitizeAbhaErrorMessage(null, session))
  }

  return { session, modalStep }
}

export const startExistingAbhaSearchJourney = async ({
  phone,
  searchParams = {},
}: {
  phone: string
  searchParams?: AbhaEntryParams
}): Promise<{ session: AbhaSessionState }> => {
  const journeyResponse = await createAbhaJourney({
    ...buildTransactionPayload({ phone, searchParams, relationship: 'Self' }),
    journey_type: 'abha_mobile_fetch',
  })

  if (!journeyResponse.redirect_url) {
    throw new AbhaFlowError('Unable to start ABHA journey. Please try again.')
  }

  const initialSession = parseAbhaEnrolRedirectUrl(journeyResponse.redirect_url)
  const { session } = await advanceAbhaNextStep(initialSession, '', [])

  return { session }
}

export const searchExistingAbhaAccounts = async (session: AbhaSessionState): Promise<ExistingAbhaSearchResult> => {
  try {
    const { session: searchSession } = await advanceAbhaNextStep(
      session,
      AbhaJourneyNodeId.MobileSearchV2,
      session.abha_member_list_details ?? []
    )

    return {
      session: searchSession,
      abhaMemberListDetails: searchSession.abha_member_list_details ?? [],
    }
  } catch {
    return {
      session: {
        ...session,
        current_node_id: AbhaJourneyNodeId.MobileSearchV2,
        next_node_id: AbhaJourneyNodeId.MobileAccountSelectionV2,
        abha_member_list_details: [],
      },
      abhaMemberListDetails: [],
    }
  }
}

const isSameAccount = (member: AbhaMemberListDetail, accountIndex: number) =>
  Number(member?.account_index) === accountIndex

const mergeExistingAbhaMembers = (
  members: AbhaMemberListDetail[],
  updates: AbhaMemberListDetail[]
): AbhaMemberListDetail[] =>
  members.map(member => {
    const update = updates.find(item => Number(item?.account_index) === Number(member?.account_index))

    return update ? { ...member, ...update } : member
  })

const buildExistingAbhaSelection = (
  members: AbhaMemberListDetail[],
  accountIndex: number,
  otp?: string
): AbhaMemberListDetail[] => {
  const selectedMembers = members.filter(member => isSameAccount(member, accountIndex) && !member?.token)

  if (selectedMembers.length !== 1) {
    throw new AbhaFlowError('Select one unverified ABHA card to continue.')
  }

  return members.map(member => {
    const isSelected = isSameAccount(member, accountIndex) && !member?.token
    const nextMember = {
      ...member,
      selected: isSelected,
    }

    if (isSelected && otp) {
      return { ...nextMember, otp }
    }

    const memberWithoutOtp = { ...nextMember }
    delete memberWithoutOtp.otp

    return memberWithoutOtp
  })
}

const mergeExistingAbhaSession = (
  previousSession: AbhaSessionState,
  nextSession: AbhaSessionState,
  sentMembers: AbhaMemberListDetail[]
): AbhaSessionState => ({
  ...nextSession,
  abha_member_list_details: mergeExistingAbhaMembers(
    sentMembers,
    nextSession?.abha_member_list_details ?? previousSession?.abha_member_list_details ?? []
  ),
})

export const selectExistingAbhaAccount = async (session: AbhaSessionState, accountIndex: number) => {
  const members = buildExistingAbhaSelection(session?.abha_member_list_details ?? [], accountIndex)
  const { session: nextSession } = await advanceAbhaNextStep(
    session,
    AbhaJourneyNodeId.MobileAccountSelectionV2,
    members
  )

  return mergeExistingAbhaSession(session, nextSession, members)
}

export const verifyExistingAbhaLoginOtp = async (session: AbhaSessionState, accountIndex: number, otp: string) => {
  const members = buildExistingAbhaSelection(session?.abha_member_list_details ?? [], accountIndex, otp)
  const { session: nextSession } = await advanceAbhaNextStep(
    session,
    AbhaJourneyNodeId.MobileLoginOtpVerificationV2,
    members
  )

  return mergeExistingAbhaSession(session, nextSession, members)
}

export const resendExistingAbhaLoginOtp = async (session: AbhaSessionState, accountIndex: number) => {
  const members = buildExistingAbhaSelection(session?.abha_member_list_details ?? [], accountIndex)
  const { session: nextSession } = await advanceAbhaNextStep(session, AbhaJourneyNodeId.ResendMobileLoginOtpV2, members)

  return mergeExistingAbhaSession(session, nextSession, members)
}

export const startAbhaHeroJourney = async ({
  phone,
  aadhaar,
  searchParams,
}: {
  phone: string
  aadhaar?: string
  searchParams: AbhaEntryParams
}): Promise<{ session: AbhaSessionState; modalStep: AbhaHeroModalStep }> => {
  const journeyResponse = await createAbhaJourney({
    ...buildTransactionPayload({ phone, aadhaar, searchParams }),
    journey_type: 'abha_multiple_self',
  })

  if (!journeyResponse.redirect_url) {
    throw new AbhaFlowError('Unable to start ABHA journey. Please try again.')
  }

  const initialSession = parseAbhaEnrolRedirectUrl(journeyResponse.redirect_url)
  const { session } = await advanceAbhaNextStep(initialSession, '')
  const modalStep = resolveModalStepFromNodeId(session.next_node_id)

  if (modalStep === AbhaHeroModalStep.Error) {
    throw new AbhaFlowError(sanitizeAbhaErrorMessage(null, session))
  }

  return { session, modalStep }
}

export const shouldUseMockAbhaFlow = (session: AbhaSessionState | null | undefined) => !session?.transaction_id
